'use client'

import React, { forwardRef } from 'react'

import {
  affixStyles,
  fieldStyles,
  inputStyles,
  labelStyles,
  ornamentSize,
  ornamentStyles,
} from './styles'
import { Icon } from '@sidecar-components/atoms/Icon'
import { IconButton } from '@sidecar-components/molecules/IconButton'
import { InputHelperText } from '@sidecar-components/atoms/InputHelperText'
import { TextFieldProps, TextFieldTypes } from './TextField.types'

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      autoComplete,
      autoFocus,
      button,
      buttonLabel,
      buttonSize,
      className,
      condensed,
      testId,
      disabled,
      error,
      helperText,
      icon,
      inputMode,
      label,
      name,
      onBlur,
      onButtonClick,
      onChange,
      onFocus,
      onKeyDown,
      maxLength,
      pattern,
      placeholder,
      prefix,
      suffix,
      textRedacted,
      type = TextFieldTypes.TEXT,
      value,
    },
    ref,
  ) => {
    // Check if field has a value (including 0)
    const hasValue = value !== undefined && value !== null && value !== ''

    return (
      <div className={className}>
        <div className={fieldStyles}>
          {/* Icons are like a '$' you might find on a currency field, on left */}
          {!!icon && (
            <Icon
              name={icon}
              size={ornamentSize(condensed)}
              className={`${ornamentStyles} left-4 color-icon-default`}
            />
          )}

          {/* we're using ternary with null here because false is not a valid
            aria value. Setting to null means the value won't render */}
          <input
            ref={ref}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            aria-describedby={helperText ? `${name}-helper` : undefined}
            aria-disabled={disabled}
            aria-invalid={!!error}
            aria-labelledby={name ? `${name}-label` : undefined}
            className={inputStyles({
              button,
              condensed,
              disabled,
              error,
              icon,
              label,
              prefix,
              suffix,
            })}
            data-testid={testId}
            disabled={disabled}
            id={name}
            inputMode={inputMode}
            name={name}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            maxLength={maxLength}
            pattern={pattern}
            placeholder={placeholder}
            type={type}
            value={value}
            onKeyDown={onKeyDown}
            data-heap-redact-text={textRedacted}
          />

          {/* A prefix is fixed text like a '$' on a currency field, on left.
            Use it in place of an icon, not alongside one. */}
          {!!prefix && !icon && (
            <span
              aria-hidden="true"
              className={affixStyles({ condensed, value: hasValue })}
            >
              {prefix}
            </span>
          )}

          {/* A suffix is fixed text like a '%' on a percentage field, on right.
            Use it in place of a button, not alongside one. */}
          {!!suffix && !button && (
            <span
              aria-hidden="true"
              className={affixStyles({
                condensed,
                side: 'right',
                value: hasValue,
              })}
            >
              {suffix}
            </span>
          )}

          {/* The label acts kind of like a placeholder, except it moves out of
            the way. It's underneath the input so that we can use peer-* 
            classes to style things */}
          {!!label && (
            <label
              htmlFor={name}
              id={`${name}-label`}
              className={labelStyles({
                condensed,
                error,
                icon,
                value: hasValue,
              })}
            >
              {label}
            </label>
          )}

          {/* Buttons are like an 'x' you use to clear a field, on right. */}
          {!!button && (
            <IconButton
              icon={button}
              label={buttonLabel || button}
              onClick={onButtonClick}
              className={`${ornamentStyles} right-4`}
              size={buttonSize || ornamentSize(condensed)}
              type="button" // we don't want forms to guess it's a submit button
              id={`${name}-btn`}
              disabled={disabled}
            />
          )}
        </div>

        {/* The error and helper text will be separate from the input just so
          we keep our sanity when trying to position everything. */}
        <InputHelperText name={name} error={error} helperText={helperText} />
      </div>
    )
  },
)

TextField.displayName = 'TextField'
